import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Zap, ChevronDown, ChevronUp, Filter, Download, Upload, Star, Wifi, Home, Briefcase, Sliders, Table, Grid } from 'lucide-react';
import { comparisonModalVariants, contentVariants, buttonHoverVariants } from './utils/animations';
import PackageCard from './components/PackageCard';
import ComparisonTable from './components/ComparisonTable';
import useComparisonStore from './store/comparisonStore';
import { useClarityModalTracking } from '../../hooks/useClarityModalTracking';
import { trackEvent, trackJourneyStep } from '../../config/clarity';

/**
 * ComparisonModal component
 * 3/4 width modal that slides in from the left
 * Shows comparison of packages based on selected criteria
 * 
 * Now using Zustand for state management
 */
const ComparisonModal = ({
  isOpen,
  onClose,
  onSelectPackage = () => {}
}) => {
  // Get state and actions from the Zustand store
  const {
    selectedOperators,
    recommendedSpeed,
    priceRange,
    speedRange,
    openRecommendationModal
  } = useComparisonStore();
  
  // Local state for UI
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'table'
  const [homePackagesExpanded, setHomePackagesExpanded] = useState(true);
  const [businessPackagesExpanded, setBusinessPackagesExpanded] = useState(true);
  const [sortBy, setSortBy] = useState('price');
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedPackages, setSelectedPackages] = useState([]);
  const [filteredPackages, setFilteredPackages] = useState([]);
  
  // Track modal open/close
  const { trackAction } = useClarityModalTracking('comparison_modal_component', isOpen, {
    selectedOperatorsCount: selectedOperators?.length || 0,
    recommendedSpeed,
    viewMode
  });
  
  // Check if we're on mobile
  const isMobile = windowWidth < 768;
  
  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      // Save the current scroll position
      const scrollY = window.scrollY;
      
      // Prevent scrolling
      document.body.style.overflow = 'hidden';
      
      return () => {
        // Restore scrolling when component unmounts or modal closes
        document.body.style.overflow = '';
        
        // Restore scroll position
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);
  
  // Clear selected packages
  const clearSelectedPackages = () => {
    // Track clearing selections
    trackEvent('comparison_selections_cleared', {
      packageCount: selectedPackages.length,
      packages: selectedPackages.map(p => p.operatorName).join(',')
    });
    
    setSelectedPackages([]);
  };
  
  // Toggle package selection for comparison table
  const togglePackageSelection = (pkg) => {
    const isAlreadySelected = selectedPackages.some(p => p.operatorName === pkg.operatorName && p.Price === pkg.Price);
    
    if (isAlreadySelected) {
      // Track package removal from comparison
      trackEvent('package_removed_from_comparison', {
        packageName: pkg.operatorName,
        packagePrice: pkg.Price,
        packageSpeed: pkg.DownloadSpeed,
        currentComparisonCount: selectedPackages.length - 1
      });
      
      // Remove package
      setSelectedPackages(selectedPackages.filter(
        p => !(p.operatorName === pkg.operatorName && p.Price === pkg.Price)
      ));
    } else {
      // Add package (limit to 5)
      if (selectedPackages.length < 5) {
        // Track package addition to comparison
        trackEvent('package_added_to_comparison', {
          packageName: pkg.operatorName,
          packagePrice: pkg.Price,
          packageSpeed: pkg.DownloadSpeed,
          currentComparisonCount: selectedPackages.length + 1
        });
        
        setSelectedPackages([...selectedPackages, pkg]);
        
        // Track journey step if this is the first package
        if (selectedPackages.length === 0) {
          trackJourneyStep('package_selection', 'started_comparison', {
            firstPackage: pkg.operatorName,
            packageSpeed: pkg.DownloadSpeed
          });
        }
      } else {
        // Track max packages alert
        trackEvent('max_packages_alert', {
          attemptedPackage: pkg.operatorName
        });
        
        // Show alert if trying to select more than 5 packages
        alert('You can select a maximum of 5 packages for comparison.');
      }
    }
  };
  
  // Handle sort change
  const handleSortChange = (newSortBy) => {
    // Track sort change
    trackEvent('comparison_sort_changed', {
      previousSortBy: sortBy,
      newSortBy,
      newDirection: sortBy === newSortBy && sortDirection === 'asc' ? 'desc' : 'asc'
    });
    
    if (sortBy === newSortBy) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortDirection('asc');
    }
  };
  
  // Handle recommendation button click
  const handleRecommendClick = () => {
    // Track recommendation request
    trackEvent('recommendation_requested', {
      source: 'comparison_modal',
      currentViewMode: viewMode,
      selectedPackagesCount: selectedPackages.length
    });
    
    trackJourneyStep('package_selection', 'requesting_recommendation', {
      fromComparisonModal: true
    });
    
    openRecommendationModal();
    onClose();
  };
  
  // Use filtered packages from the Zustand store
  useEffect(() => {
    if (isOpen) {
      // Get the filtered packages from the store
      const storeFilteredPackages = useComparisonStore.getState().filteredPackages;
      
      // Map the packages to the format expected by the component
      const formattedPackages = storeFilteredPackages.map(pkg => ({
        operatorName: pkg.operatorName,
        connectionType: pkg.connectionType || 'FTTH',
        Price: pkg.Price,
        DownloadSpeed: pkg["Download speed (kbps)"] / 1024, // Convert to Mbps
        UploadSpeed: pkg["Upload speed (kbps)"] / 1024, // Convert to Mbps
        features: [
          'Uncapped',
          pkg["Download speed (kbps)"] === pkg["Upload speed (kbps)"] ? 'Symmetrical' : 'Asymmetrical',
          'Free Router'
        ],
        // Keep the original package data for reference
        originalPackage: pkg
      }));
      
      setFilteredPackages(formattedPackages);
    }
  }, [isOpen, selectedOperators, priceRange, speedRange]);
  
  // Group packages by type
  const homePackages = filteredPackages.filter(pkg => pkg.connectionType !== 'FTTB');
  const businessPackages = filteredPackages.filter(pkg => pkg.connectionType === 'FTTB');
  
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop - only on mobile */}
          {isMobile && (
            <motion.div
              className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm"
              style={{ zIndex: 44 }} // Lower z-index for backdrop, below the modal
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => {
                trackAction('backdrop_close');
                onClose();
              }}
            />
          )}
          
          {/* Modal */}
          <motion.div
            className={`fixed top-0 bottom-0 left-0 bg-gradient-to-b from-gray-900 to-gray-800 shadow-2xl border-r border-gray-800 flex flex-col ${
              isMobile ? 'w-full' : 'w-3/4'
            }`}
            style={{
              zIndex: 45, // Much lower z-index to ensure SelectionModal is on top
              width: isMobile ? '100%' : 'calc(75% - 1px)',
              boxShadow: '0 0 10px rgba(0, 0, 0, 0.2)',
              right: isMobile ? 'auto' : 'calc(25% + 1px)' // Make sure it doesn't overlap with SelectionModal
            }}
            variants={comparisonModalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {/* Header */}
            <motion.div 
              className="p-4 border-b border-gray-800 flex justify-between items-center"
              variants={contentVariants}
            >
              <div className="flex items-center">
                <Zap className="h-5 w-5 text-purple-400 mr-2" />
                <h2 className="text-xl font-bold text-white">Compare Fibre Packages</h2>
              </div>
              <button 
                className="text-gray-400 hover:text-white p-1 rounded-full hover:bg-gray-800/50 transition-colors"
                onClick={() => {
                  trackAction('x_button_close');
                  onClose();
                }}
              >
                <X className="h-5 w-5" />
              </button>
            </motion.div>
            
            {/* Header with current filter info */}
            <motion.div 
              className="bg-gray-800/50 p-4 border-b border-gray-800"
              variants={contentVariants}
            >
              <div className={`${isMobile ? 'flex flex-col space-y-3' : 'flex flex-wrap items-center justify-between gap-4'}`}>
                <div>
                  <h3 className="text-lg font-medium text-white">Fibre Packages</h3>
                  <div className="text-sm text-gray-400 mt-1">
                    Showing {filteredPackages.length} packages
                    {recommendedSpeed && (
                      <span className="ml-1 text-green-400">
                        (Recommended: {recommendedSpeed} Mbps)
                      </span>
                    )}
                  </div>
                </div>
                
                {/* View Mode Toggle and Clear Selections */}
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-400 mr-1">View:</span>
                  <button
                    className={`p-1.5 rounded-md ${viewMode === 'grid' ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-300'}`}
                    onClick={() => {
                      trackEvent('view_mode_changed', {
                        previousMode: viewMode,
                        newMode: 'grid'
                      });
                      setViewMode('grid');
                    }}
                    title="Grid View"
                  >
                    <Grid className="h-4 w-4" />
                  </button>
                  <button
                    className={`p-1.5 rounded-md ${viewMode === 'table' ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-300'}`}
                    onClick={() => {
                      trackEvent('view_mode_changed', {
                        previousMode: viewMode,
                        newMode: 'table'
                      });
                      setViewMode('table');
                    }}
                    title="Table View"
                  >
                    <Table className="h-4 w-4" />
                  </button>
                  
                  {selectedPackages.length > 0 && (
                    <button
                      className="ml-2 px-2 py-1 text-xs bg-gray-700 text-gray-300 hover:bg-gray-600 rounded-md"
                      onClick={clearSelectedPackages}
                      title="Clear Selections"
                    >
                      Clear Selections
                    </button>
                  )}
                </div>
                
                {/* Sort Controls */}
                <div className="flex items-center text-sm text-gray-400">
                  <span className="mr-2">Sort by:</span>
                  <div className="flex rounded-lg overflow-hidden">
                    <button
                      className={`px-3 py-1.5 text-xs font-medium flex items-center ${
                        sortBy === 'price'
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                      onClick={() => handleSortChange('price')}
                    >
                      Price
                      {sortBy === 'price' && (
                        sortDirection === 'asc'
                          ? <ChevronUp className="h-3 w-3 ml-1" />
                          : <ChevronDown className="h-3 w-3 ml-1" />
                      )}
                    </button>
                    <button
                      className={`px-3 py-1.5 text-xs font-medium flex items-center ${
                        sortBy === 'speed'
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                      onClick={() => handleSortChange('speed')}
                    >
                      Speed
                      {sortBy === 'speed' && (
                        sortDirection === 'asc'
                          ? <ChevronUp className="h-3 w-3 ml-1" />
                          : <ChevronDown className="h-3 w-3 ml-1" />
                      )}
                    </button>
                    <button
                      className={`px-3 py-1.5 text-xs font-medium flex items-center ${
                        sortBy === 'type'
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                      onClick={() => handleSortChange('type')}
                    >
                      Type
                      {sortBy === 'type' && (
                        sortDirection === 'asc'
                          ? <ChevronUp className="h-3 w-3 ml-1" />
                          : <ChevronDown className="h-3 w-3 ml-1" />
                      )}
                    </button>
                  </div>
                </div>
                
                {/* Get a Recommendation Link */}
                <button
                  onClick={handleRecommendClick}
                  className="mt-4 w-full py-2 px-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg text-sm font-medium flex items-center justify-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  Get a Speed Recommendation
                </button>
              </div>
            </motion.div>
            
            {/* Package List */}
            <div className="flex-1 overflow-y-auto p-4">
              {filteredPackages.length === 0 ? (
                <motion.div 
                  className="text-center py-12"
                  variants={contentVariants}
                >
                  <div className="text-gray-400 mb-2">No packages match your filters</div>
                  <motion.button
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors"
                    onClick={() => {
                      // Reset filters
                      // This would call resetFilters from the Zustand store in a real implementation
                    }}
                    variants={buttonHoverVariants}
                    initial="initial"
                    whileHover="hover"
                    whileTap="tap"
                  >
                    Reset Filters
                  </motion.button>
                </motion.div>
              ) : (
                <>
                  {/* Table View */}
                  {viewMode === 'table' && (
                    <motion.div
                      className="mb-8"
                      variants={contentVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                    >
                      {selectedPackages.length > 0 ? (
                        <>
                          <ComparisonTable
                            packages={selectedPackages}
                            onSelectPackage={(pkg) => onSelectPackage(pkg, 'availability')}
                          />
                          
                          <div className="mt-4 text-center flex justify-center space-x-4">
                            <motion.button
                              className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors"
                              onClick={() => {
                                trackEvent('view_mode_changed', {
                                  previousMode: 'table',
                                  newMode: 'grid',
                                  context: 'from_table_view_button'
                                });
                                setViewMode('grid');
                              }}
                              variants={buttonHoverVariants}
                              initial="initial"
                              whileHover="hover"
                              whileTap="tap"
                            >
                              Back to Grid View
                            </motion.button>
                            
                            <motion.button
                              className="px-4 py-2 bg-gray-700 text-white rounded-lg text-sm font-medium hover:bg-gray-600 transition-colors"
                              onClick={clearSelectedPackages}
                              variants={buttonHoverVariants}
                              initial="initial"
                              whileHover="hover"
                              whileTap="tap"
                            >
                              Clear Selections
                            </motion.button>
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-12 bg-gray-800 rounded-lg">
                          <div className="text-gray-300 mb-4">
                            <p className="text-lg font-medium mb-2">No packages selected for comparison</p>
                            <p className="text-sm text-gray-400">Select up to 5 packages to compare them side by side</p>
                          </div>
                          <motion.button
                            className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors"
                            onClick={() => {
                              trackEvent('view_mode_changed', {
                                previousMode: 'table',
                                newMode: 'grid',
                                context: 'from_empty_table_view'
                              });
                              setViewMode('grid');
                            }}
                            variants={buttonHoverVariants}
                            initial="initial"
                            whileHover="hover"
                            whileTap="tap"
                          >
                            Go to Grid View
                          </motion.button>
                        </div>
                      )}
                    </motion.div>
                  )}
                  
                  {/* Grid View */}
                  {viewMode === 'grid' && (
                    <motion.div
                      variants={contentVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                    >
                      {/* Home Packages */}
                      {homePackages.length > 0 && (
                        <>
                          <div
                            className="flex items-center justify-between cursor-pointer mb-4"
                            onClick={() => {
                              if (isMobile) {
                                trackEvent('section_toggle', {
                                  section: 'home_packages',
                                  newState: !homePackagesExpanded ? 'expanded' : 'collapsed'
                                });
                                setHomePackagesExpanded(!homePackagesExpanded);
                              }
                            }}
                          >
                            <h3 className="text-xl font-semibold text-white flex items-center">
                              <Home className="h-5 w-5 text-purple-400 mr-2" />
                              Home Packages
                            </h3>
                            {isMobile && (
                              homePackagesExpanded ?
                                <ChevronUp className="h-5 w-5 text-gray-400" /> :
                                <ChevronDown className="h-5 w-5 text-gray-400" />
                            )}
                          </div>
                          
                          {(!isMobile || homePackagesExpanded) && (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
                              {homePackages.map((pkg, index) => {
                                const isSelected = selectedPackages.some(
                                  p => p.operatorName === pkg.operatorName && p.Price === pkg.Price
                                );
                                
                                return (
                                  <PackageCard
                                    key={`home-${index}`}
                                    pkg={pkg}
                                    index={index}
                                    onSelect={(pkg, action) => {
                                      if (action === 'compare') {
                                        togglePackageSelection(pkg);
                                      } else if (action === 'availability') {
                                        // Explicitly pass 'availability' action to ensure the coverage sidebar opens
                                        onSelectPackage(pkg, 'availability');
                                      } else {
                                        onSelectPackage(pkg);
                                      }
                                    }}
                                    isSelected={isSelected}
                                    isDisabled={viewMode === 'table' && selectedPackages.length >= 5 && !isSelected}
                                    disabledReason="Max 5 packages"
                                  />
                                );
                              })}
                            </div>
                          )}
                        </>
                      )}
                      
                      {/* Business Packages */}
                      {businessPackages.length > 0 && (
                        <>
                          <div
                            className="flex items-center justify-between cursor-pointer mb-4"
                            onClick={() => {
                              if (isMobile) {
                                trackEvent('section_toggle', {
                                  section: 'business_packages',
                                  newState: !businessPackagesExpanded ? 'expanded' : 'collapsed'
                                });
                                setBusinessPackagesExpanded(!businessPackagesExpanded);
                              }
                            }}
                          >
                            <h3 className="text-xl font-semibold text-white flex items-center">
                              <Briefcase className="h-5 w-5 text-blue-400 mr-2" />
                              Business Packages
                            </h3>
                            {isMobile && (
                              businessPackagesExpanded ?
                                <ChevronUp className="h-5 w-5 text-gray-400" /> :
                                <ChevronDown className="h-5 w-5 text-gray-400" />
                            )}
                          </div>
                          
                          {(!isMobile || businessPackagesExpanded) && (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                              {businessPackages.map((pkg, index) => {
                                const isSelected = selectedPackages.some(
                                  p => p.operatorName === pkg.operatorName && p.Price === pkg.Price
                                );
                                
                                return (
                                  <PackageCard
                                    key={`business-${index}`}
                                    pkg={pkg}
                                    index={index}
                                    onSelect={(pkg, action) => {
                                      if (action === 'compare') {
                                        togglePackageSelection(pkg);
                                      } else if (action === 'availability') {
                                        // Explicitly pass 'availability' action to ensure the coverage sidebar opens
                                        onSelectPackage(pkg, 'availability');
                                      } else {
                                        onSelectPackage(pkg);
                                      }
                                    }}
                                    isSelected={isSelected}
                                    isDisabled={viewMode === 'table' && selectedPackages.length >= 5 && !isSelected}
                                    disabledReason="Max 5 packages"
                                  />
                                );
                              })}
                            </div>
                          )}
                        </>
                      )}
                    </motion.div>
                  )}
                </>
              )}
            </div>
            
            {/* Floating Compare Button */}
            {viewMode === 'grid' && selectedPackages.length >= 2 && (
              <motion.div
                className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50"
                initial={{ y: 100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 100, opacity: 0 }}
              >
                <div className="flex space-x-2">
                  <motion.button
                    className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full shadow-lg font-medium flex items-center"
                    onClick={() => {
                      trackEvent('view_mode_changed', {
                        previousMode: 'grid',
                        newMode: 'table',
                        context: 'from_floating_button',
                        selectedPackagesCount: selectedPackages.length
                      });
                      
                      // Track journey step for comparison
                      trackJourneyStep('package_selection', 'viewing_comparison_table', {
                        packageCount: selectedPackages.length,
                        packages: selectedPackages.map(p => p.operatorName).join(',')
                      });
                      
                      setViewMode('table');
                    }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Table className="h-5 w-5 mr-2" />
                    Compare {selectedPackages.length} Packages
                  </motion.button>
                  
                  <motion.button
                    className="px-3 py-3 bg-gray-700 text-white rounded-full shadow-lg font-medium"
                    onClick={clearSelectedPackages}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    title="Clear Selections"
                  >
                    <X className="h-5 w-5" />
                  </motion.button>
                </div>
              </motion.div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ComparisonModal;